import axios from 'axios';
import {getStoredTokens, setStoredTokens} from "@lib/auth";

// Reference: https://medium.com/@cristafovici.den/master-data-fetching-with-axios-and-react-query-in-2024-part-1-7b10c5909eb1

export const API_URLS = {
  ACTIVITIES: 'activities',
  AUTH: 'auth',
  EVENTS: 'events',
  SESSIONS: 'sessions',
  CURRICULA: 'curricula',
  PARTICIPANTS: 'participants',
  FORMS: 'forms',
}

const axiosClient = (() => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Accept: "application/json, text/plain, */*",
    },
  });
})();

/**
 * Automatically inject auth tokens with request
 */
axiosClient.interceptors.request.use(
  (config) => {
    if (!config.anonymous) {
      const {accessToken} = getStoredTokens();
      // console.log('[axios] Request with auth token ', accessToken.substring(accessToken.length - 20));
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// If the request failed due to expired auth token, request a new auth token and re-run the request.
axiosClient.interceptors.response.use(
  (res) => {
    // Response was OK the first time. No refresh needed.
    return res;
  },
  async (err) => {
    const status = err.response ? err.response.status : null;
    const originalRequest = err.config;

    if (status === 401 && !originalRequest.noRefresh) {
      try {
        const { refreshToken } = getStoredTokens();
        const response = await axiosClient.post(
          '/auth/refresh',
          {
            token: refreshToken,
          },
        );
        // console.log('[axios] refresh response', response);
        const { AccessToken: accessToken, IdToken: idToken } = response.data.data;
        // Note: Refresh endpoint doesn't send a new refresh token, only access and ID.
        // We continue using the existing refresh token.
        setStoredTokens({
          accessToken,
          idToken,
        });
        axiosClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        return await axiosClient({
          ...originalRequest,
          noRefresh: true,
        });
      } catch (error) {
        return Promise.reject(error);
      }
    }

    if (status === 403 && err.response.data) {
      return Promise.reject(err.response.data);
    }

    return Promise.reject(err);
  }
);

export const axiosRequest = async (options) => {
  const onSuccess = (response) => {
    // console.log('[axios] response', response);
    const { data } = response;
    // API JSON responses will have a "data" property. Other types of responses, like
    // downloadable files, won't.
    return data.data || data;
  };

  const onError = function (error) {
    // console.log('[axios] onError', error, error.response);

    const errors = error.response ? [
      error.response?.statusText,
      error.response?.data?.message,
      ...error.response?.data?.errors || [],
    ].filter((e) => !!e) : [
      // If there was a network error (i.e., server is down), error.response will be undefined.
      // We can read a message in error.message.
      error.message,
    ];

    return Promise.reject({
      // Note: status will be undefined if there was a network error (e.g., API is down)
      status: error.status,
      errors
    });
  };

  return axiosClient(options).then(onSuccess).catch(onError);
};

export default axiosClient;
